/**
 * The spoiler guard for everything shown before you play (Today's hero board and its
 * level formes): a puzzle's STRUCTURE with every letter stripped out.
 *
 * How it's guaranteed:
 * - `boardStructure` copies, field by field, only geometry: the grid size, each cell's
 *   kind, where each clue's arrow sits and which way it points, each answer's id, axis
 *   and cell indexes, and which cells are already solved (as indexes, never letters).
 *   It never reads `answer.text`, `solution`, the grid strings or clue content, so
 *   there is nothing in it to leak. `BoardStructure` has no field that could hold one.
 * - The hero board (`HeroBoard`) accepts only a `BoardStructure`, never a puzzle or a
 *   model, so the type system keeps solutions out of it.
 * - The mini maps reuse `BoardMap`, which takes a `PuzzleModel`; `structureModel` gives
 *   it one with every answer, the solution, the grid letters and the clue text blanked.
 * Tests assert that neither holds any answer (structure.test.ts).
 */
import type { PuzzleModel } from '@arrow/game-engine';
import type { Axis, CellKind, Puzzle } from '@arrow/puzzle-schema';

import { type ArrowShape, arrowAnchor } from '../game/view.ts';

export interface StructureClue {
	/** Flat index of the clue cell. */
	cell: number;
	/** Which half of the clue cell (0 = top), of `count`. */
	slot: number;
	count: number;
	answerId: string;
	/** The wedge: its root on the clue/answer boundary (board units, a cell = 100) and shape. */
	arrow: { x: number; y: number; shape: ArrowShape };
}

export interface StructureAnswer {
	id: string;
	axis: Axis;
	cells: number[];
}

/** A board with no letters in it: only what a blank printed forme shows. */
export interface BoardStructure {
	width: number;
	height: number;
	kinds: CellKind[];
	clues: StructureClue[];
	answers: StructureAnswer[];
	/** Cells of answers already solved (shown pressed, never with their letters). */
	pressed: number[];
}

export function boardStructure(model: PuzzleModel, solvedAnswers: readonly string[] = []): BoardStructure {
	const clues: StructureClue[] = [];
	for (const [cellKey, ids] of Object.entries(model.cluesByCell)) {
		ids.forEach((id, slot) => {
			const clue = model.clues[id];
			if (!clue) return;
			const { x, y, shape } = arrowAnchor(model, clue);
			clues.push({ cell: Number(cellKey), slot, count: ids.length, answerId: clue.answerId, arrow: { x, y, shape } });
		});
	}
	const answers: StructureAnswer[] = model.answerOrder.flatMap((id) => {
		const a = model.answers[id];
		return a ? [{ id: a.id, axis: a.axis, cells: [...a.cells] }] : [];
	});
	const pressed = new Set<number>();
	for (const id of solvedAnswers) for (const c of model.answers[id]?.cells ?? []) pressed.add(c);
	return {
		width: model.width,
		height: model.height,
		kinds: [...model.kinds],
		clues,
		answers,
		pressed: [...pressed].sort((a, b) => a - b),
	};
}

/**
 * A `PuzzleModel` with every letter and clue blanked, for `BoardMap` (the level formes,
 * the Play thumbnails): geometry, kinds and arrows survive; answers, the solution, the
 * grid's letters and every clue's text don't.
 */
export function structureModel(model: PuzzleModel): PuzzleModel {
	const answers: PuzzleModel['answers'] = {};
	for (const [id, a] of Object.entries(model.answers)) answers[id] = { ...a, cells: [...a.cells], text: '' };
	const clues: PuzzleModel['clues'] = {};
	for (const [id, c] of Object.entries(model.clues)) clues[id] = { ...c, content: { kind: 'text', text: '' } };
	const { theme: _theme, notes: _notes, ...bare } = model.puzzle;
	const puzzle: Puzzle = {
		...bare,
		grid: model.puzzle.grid.map((row) => row.replace(/[A-Za-z]/g, '_')),
		answers: model.puzzle.answers.map(({ display: _display, ...a }) => ({ ...a, text: '' })),
		clues: model.puzzle.clues.map((c) => ({ ...c, content: { kind: 'text', text: '' } })),
	};
	return { ...model, puzzle, solution: model.solution.map(() => ''), answers, clues };
}

/** Each answer's midpoint, in board units (a cell = 100). */
export function answerMid(s: Pick<BoardStructure, 'width'>, cells: readonly number[]): [number, number] {
	const a = cells[0] ?? 0;
	const b = cells[cells.length - 1] ?? a;
	const w = s.width;
	return [(((a % w) + (b % w)) / 2) * 100 + 50, ((Math.floor(a / w) + Math.floor(b / w)) / 2) * 100 + 50];
}

/**
 * A chain of answers, each crossing the last: a Flow-like walk from structure alone
 * (the mock's `chain`). Deterministic for a seed, never the solve order.
 */
export function crossingChain(s: BoardStructure, start: string, n: number, seed = 1): string[] {
	const byId = new Map(s.answers.map((a) => [a.id, a]));
	const byCell = new Map<number, string[]>();
	for (const a of s.answers) for (const c of a.cells) byCell.set(c, [...(byCell.get(c) ?? []), a.id]);
	const out = [start];
	const used = new Set(out);
	let cur = start;
	let k = seed;
	while (out.length < n) {
		const cand: string[] = [];
		for (const c of byId.get(cur)?.cells ?? []) for (const id of byCell.get(c) ?? []) if (!used.has(id) && !cand.includes(id)) cand.push(id);
		if (!cand.length) break;
		k = (k * 9301 + 49297) % 233280;
		cur = cand[k % cand.length]!;
		out.push(cur);
		used.add(cur);
	}
	return out;
}
